import React, { useEffect, useRef, useState } from "react";
import { knownForData } from "../../data/home";

/* "We Are Known For" — one proof-point at a time:
   heading + glossy icon fade in → sentence types out → reading pause →
   the whole card fades away → next card. Content from
   src/data/home/knownFor.json (backend-overridable); `icon` is a key here. */

const sp = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.9, strokeLinecap: "round", strokeLinejoin: "round" };

const KF_ICONS = {
  shield: (<svg {...sp}><path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5l-8-3z" /><path d="m9 12 2 2 4-4" /></svg>),
  briefcase: (<svg {...sp}><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M3 12h18" /></svg>),
  bars: (<svg {...sp}><path d="M7 21V11" /><path d="M12 21V4" /><path d="M17 21v-7" /><path d="M3 21h18" /></svg>),
  users: (<svg {...sp}><circle cx="9" cy="8" r="3.2" /><path d="M3.5 20a5.5 5.5 0 0 1 11 0" /><path d="M16 5.2a3.2 3.2 0 0 1 0 5.6" /><path d="M17.5 14.4A5.5 5.5 0 0 1 20.5 19" /></svg>),
};

const KnownFor = ({ data }) => {
  const src = data || knownForData;
  const ITEMS = src.items || [];
  const [idx, setIdx] = useState(0);
  const [typed, setTyped] = useState("");
  const [vis, setVis] = useState(false);
  const [typing, setTyping] = useState(false);
  const reduce = useRef(false);

  useEffect(() => {
    reduce.current = typeof window !== "undefined" &&
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  useEffect(() => {
    let cancelled = false;
    const timers = [];
    const wait = (ms) => new Promise((r) => { timers.push(setTimeout(r, ms)); });

    (async () => {
      const text = (ITEMS[idx] || {}).text || "";
      setTyped("");
      setTyping(false);
      setVis(false);
      await wait(70);                       // paint the hidden frame first
      if (cancelled) return;
      setVis(true);                          // heading + icon fade/slide in
      await wait(reduce.current ? 200 : 540); // let the heading settle
      if (cancelled) return;

      if (reduce.current) {
        setTyped(text);
      } else {
        setTyping(true);
        for (let n = 1; n <= text.length; n++) {
          await wait(24);
          if (cancelled) return;
          setTyped(text.slice(0, n));
        }
        setTyping(false);
      }

      await wait(Math.max(2600, text.length * 32)); // reading pause
      if (cancelled) return;
      setVis(false);                                  // fade away
      await wait(reduce.current ? 200 : 640);
      if (cancelled) return;
      setIdx((p) => (p + 1) % (ITEMS.length || 1));
    })();

    return () => { cancelled = true; timers.forEach(clearTimeout); };
  }, [idx]);

  const item = ITEMS[idx] || ITEMS[0] || { title: "", text: "", tone: "navy", icon: "" };

  return (
    <div className="kf" aria-live="polite">
      <span className="kf__eyebrow">{src.eyebrow}</span>

      <div className={`kf__card ${vis ? "is-in" : ""}`} key={idx}>
        <span className={`kf__ic kf__ic--${item.tone}`}>{KF_ICONS[item.icon]}</span>
        <h3 className="kf__title">{item.title}</h3>
        <p className="kf__text">
          <span className="kf__ghost" aria-hidden="true">{item.text}</span>
          <span className="kf__typed">
            {typed}
            <span className={`kf__cursor ${typing ? "is-on" : ""}`} aria-hidden="true" />
          </span>
        </p>
      </div>
    </div>
  );
};

export default KnownFor;
